import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { RoleAccessService } from '@shared/auth/services/role-access.service';
import { PrivacyAnonymizerService } from './privacy-anonymizer.service';
import { StorageService } from './storage.service';

export const PRIVACY_MODE_BODY_CLASS = 'privacy-mode';

export type PrivacyMode = 'off' | 'blur' | 'fake';

export const PRIVACY_MODES: PrivacyMode[] = ['off', 'blur', 'fake'];

/**
 * Privacy mode (admin only): hides the private data (names, phones, e-mails, addresses, prices) marked with the `pii`
 * class or recognized by the global styles (see assets/scss/core/privacy-mode.scss). The data is either blurred
 * or replaced with the fake one (see PrivacyAnonymizerService). It is only visual, the data is still in the page.
 */
@Injectable({ providedIn: 'root' })
export class PrivacyModeService {
  private readonly roleAccess = inject(RoleAccessService);
  private readonly storage = inject(StorageService);
  private readonly anonymizer = inject(PrivacyAnonymizerService);

  private readonly state = signal<PrivacyMode>('off');
  readonly mode = this.state.asReadonly();
  readonly enabled = computed(() => this.state() !== 'off');

  constructor() {
    effect(() => document.body.classList.toggle(PRIVACY_MODE_BODY_CLASS, this.state() === 'blur'));
    effect(() => (this.state() === 'fake' ? this.anonymizer.start() : this.anonymizer.stop()));
  }

  get isAvailable(): boolean {
    return this.roleAccess.isAdmin();
  }

  /** Restores the choice of the current user (call after sign in and when the panel is opened). */
  refresh(): void {
    this.state.set(this.isAvailable ? this.parse(this.storage.get(this.storageKey)) : 'off');
  }

  /** Turns the mode off (e.g. on logout) without forgetting the choice of the user. */
  reset(): void {
    this.state.set('off');
  }

  setMode(mode: PrivacyMode): void {
    if (!this.isAvailable) return;

    this.state.set(mode);
    this.storage.put(this.storageKey, mode);
  }

  private parse(value: string | null | undefined): PrivacyMode {
    // '1' was saved when the mode could only blur
    if (value === '1') return 'blur';
    return PRIVACY_MODES.includes(value as PrivacyMode) ? (value as PrivacyMode) : 'off';
  }

  private get storageKey(): string {
    return `privacyMode:${this.storage.UserPerson?.id ?? 'anonymous'}`;
  }
}
